import { useContext, useEffect } from "react";
import { To, UNSAFE_NavigationContext, useLocation } from "react-router-dom";
import { requestLeave } from "shared/helpers/unsavedChanges.store";

type NavigateFn = (to: To | number, state?: unknown, opts?: unknown) => void;

/**
 * Block any navigation that leaves the current page while unsaved changes remain (KP-2453).
 *
 * The app mounts `<BrowserRouter>` rather than a data router, so react-router's
 * `unstable_useBlocker` would throw. Instead this patches `push`/`replace` on the
 * `navigator` object directly — the very object the Sidebar `<Link>`, the Header `<Link>`
 * and every `useNavigate()` call go through, so none of those components need changing.
 *
 * Only navigations to a different pathname are blocked: tab switches (`?type=`) and the
 * internal `setSearchParams` calls pass straight through, guarded separately in `Tabs.onChange`.
 */
const useNavigationBlocker = () => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const { pathname } = useLocation();

  useEffect(() => {
    const originalPush = navigator.push;
    const originalReplace = navigator.replace;

    const wrap =
      (original: NavigateFn): NavigateFn =>
      (to, state, opts) => {
        const run = () => original.call(navigator, to as To, state, opts);
        const targetPathname = getTargetPathname(to);
        // `null` means the call carries no pathname (search/hash only), so the page is not being left.
        if (targetPathname === null || targetPathname === pathname) {
          run();
          return;
        }
        requestLeave(run);
      };

    navigator.push = wrap(navigator.push as NavigateFn) as typeof navigator.push;
    navigator.replace = wrap(navigator.replace as NavigateFn) as typeof navigator.replace;

    return () => {
      navigator.push = originalPush;
      navigator.replace = originalReplace;
    };
  }, [navigator, pathname]);
};

/** Extract the target pathname from `to`; returns null when `to` carries no pathname (search/hash only). */
const getTargetPathname = (to: To | number): string | null => {
  if (typeof to === "number") return null;
  if (typeof to === "string") {
    const path = to.split("?")[0].split("#")[0];
    return path === "" ? null : path;
  }
  return to.pathname ?? null;
};

export default useNavigationBlocker;
